import {
    Button,
    DateRangeField,
    Form,
    FormFooter,
    FormSection,
    MultiSelectField,
    Page,
    SelectOption,
    toast
} from '@7shifts/sous-chef';
import { getTicketsWithMessagesReport } from 'api/reportsApi';
import { useWhatsContext } from 'contexts/WhatsAppContext/WhatsAppContext';
import { useFormik } from 'formik';
import { __ } from 'i18n/translate';
import { downloadCSVBlob } from 'utils/csv';
import { dateYearMonthDay } from 'utils/date';

const MAX_DAYS = 31;

type FormData = {
    date: { start: Date; end: Date };
    channels: SelectOption<number>[];
};

const TicketsWithMessagesPage = () => {
    const { whatsApps } = useWhatsContext();

    // Todos os canais, inclusive desconectados: um número trocado ainda tem
    // histórico de mensagens no período.
    const channelOptions: SelectOption<number>[] = whatsApps.map(
        (channel) => ({
            label: channel.actualNumber
                ? `${channel.name} (${channel.actualNumber})`
                : channel.name,
            value: channel.id
        })
    );

    const formik = useFormik<FormData>({
        initialValues: {
            date: {
                start: new Date(),
                end: new Date()
            },
            channels: []
        },
        validate: (values) => {
            const errors: Partial<Record<keyof FormData, string>> = {};
            if (!values.date?.start || !values.date?.end) {
                errors.date = __('reportsPage.reportTicketsWithMessagesDateRequired');
                return errors;
            }
            const diffMs =
                values.date.end.getTime() - values.date.start.getTime();
            const diffDays = diffMs / (1000 * 60 * 60 * 24);
            if (diffDays > MAX_DAYS) {
                errors.date = __(
                    'reportsPage.reportTicketsWithMessagesMaxDays',
                    { max: MAX_DAYS }
                );
            }
            return errors;
        },
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(true);
            getTicketsWithMessagesReport(
                dateYearMonthDay(values.date.start),
                dateYearMonthDay(values.date.end),
                values.channels.map((option) => option.value)
            )
                .then((data) => {
                    downloadCSVBlob(data, 'tickets-com-mensagens.csv');
                })
                .catch(() => {
                    toast('Something went wrong', 'danger');
                })
                .finally(() => formik.setSubmitting(false));
        }
    });

    return (
        <Page
            breadcrumbs={__('main.reports')}
            title={__('reportsPage.reportTicketsWithMessages')}
            subtitle={__('reportsPage.reportTicketsWithMessagesDescription')}
        >
            <Form formik={formik}>
                <FormSection as="card">
                    <DateRangeField
                        name="date"
                        label="Período"
                        format="dd/MM/yyyy"
                        disabledDays={{
                            after: new Date()
                        }}
                    />
                    <MultiSelectField
                        name="channels"
                        label={__('main.channel')}
                        placeholder={__('dashboardPage.allChannels')}
                        caption={__(
                            'reportsPage.reportTicketsWithMessagesChannelCaption'
                        )}
                        options={channelOptions}
                        onChange={(selected: SelectOption<number>[]) =>
                            formik.setFieldValue('channels', selected || [])
                        }
                    />
                </FormSection>
                <FormFooter
                    actions={{
                        primary: (
                            <Button type="submit" loading={formik.isSubmitting}>
                                {__('main.downloadCSV')}
                            </Button>
                        )
                    }}
                />
            </Form>
        </Page>
    );
};

export default TicketsWithMessagesPage;
